import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getCatalogo, editarPlantillaCatalogo } from '../../../services/kapsoTemplatesService';
import { useCompanies, nombreDeEmpresa } from '../useCompanies';
import { EstadoBadge, SyncBadge, DriftBadge } from './templateBadges';

/** Tope de Meta para el cuerpo de una plantilla. */
const MAX_BODY = 1024;

const Editor = ({ plantilla, companies, onGuardada }) => {
  const [texto, setTexto] = useState(plantilla.body_text);
  const [guardando, setGuardando] = useState(false);
  const sucio = texto !== plantilla.body_text;

  const guardar = async () => {
    setGuardando(true);
    try {
      const actualizada = await editarPlantillaCatalogo(plantilla.key, { body_text: texto });
      toast.success('Plantilla guardada');
      onGuardada(actualizada);
    } catch (err) {
      toast.error(err.message || 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className='border-t border-slate-100 p-4 space-y-4'>
      <div>
        <label
          htmlFor={`body-${plantilla.key}`}
          className='block text-xs font-medium text-slate-600 mb-1'
        >
          Texto del mensaje
        </label>
        <textarea
          id={`body-${plantilla.key}`}
          rows={4}
          value={texto}
          onChange={e => setTexto(e.target.value)}
          className='w-full rounded-lg border border-slate-200 p-3 text-sm font-mono focus:border-blue-400 focus:outline-none'
        />
        <div className='mt-1 flex items-center justify-between text-xs'>
          <span className='text-slate-400'>
            Las variables van entre llaves dobles: <code>{'{{customer_name}}'}</code>
          </span>
          <span className={texto.length > MAX_BODY ? 'text-red-600 font-medium' : 'text-slate-400'}>
            {texto.length} / {MAX_BODY}
          </span>
        </div>
      </div>

      {plantilla.unknown_variables?.length > 0 && (
        // Se avisa al escribirla, y no cuando Meta ya la aprobó y sale un hueco
        // delante de un deudor.
        <div className='rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800'>
          <strong>No sabemos rellenar</strong> {plantilla.unknown_variables.join(', ')}. Ese dato no
          sale del expediente: saldrá vacío.
        </div>
      )}

      <div>
        <p className='text-xs font-medium text-slate-600 mb-1'>Vista previa (del texto guardado)</p>
        <p className='rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap'>
          {plantilla.preview_text}
        </p>
      </div>

      <div>
        <p className='text-xs font-medium text-slate-600 mb-1'>
          Clientes que la tienen ({plantilla.registrations.length})
        </p>
        {plantilla.registrations.length === 0 ? (
          <p className='text-sm text-slate-400'>Ninguno todavía.</p>
        ) : (
          <ul className='space-y-1'>
            {plantilla.registrations.map(r => (
              <li key={r.company_id} className='flex items-center gap-2 text-sm text-slate-700'>
                <span className='truncate max-w-[16rem]'>{nombreDeEmpresa(companies, r.company_id)}</span>
                <EstadoBadge estado={r.approval_status} />
                <SyncBadge sync={r.sync} />
                <DriftBadge drift={r.catalogue_drift} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='flex items-center gap-3'>
        <button
          type='button'
          onClick={guardar}
          disabled={!sucio || guardando || texto.length === 0 || texto.length > MAX_BODY}
          className='px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400'
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {sucio && (
          // Editar el catálogo NO reenvía nada a Meta ni toca las copias que ya
          // tienen los clientes: propagarlo invalidaría aprobaciones en silencio.
          <span className='text-xs text-slate-500'>
            Guardar cambia el catálogo. Los clientes que ya la tienen conservan su propia copia,
            con su texto y su aprobación.
          </span>
        )}
      </div>
    </div>
  );
};

const KapsoCatalogue = () => {
  const [plantillas, setPlantillas] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const companies = useCompanies();
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setPlantillas(await getCatalogo());
      setError(null);
    } catch (err) {
      // No enseñar «no hay plantillas» cuando lo que pasa es que no se han
      // podido cargar: son cosas distintas.
      setError(err.message || 'No se han podido cargar las plantillas');
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 p-4 text-sm text-red-700'>
        {error}{' '}
        <button type='button' onClick={cargar} className='underline font-medium'>
          Reintentar
        </button>
      </div>
    );
  }

  if (!plantillas) {
    return <div className='bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-400'>Cargando…</div>;
  }

  return (
    <div className='space-y-3'>
      {/* Regla del modelo, no recuento. Decía «no está activo en ningún
          cliente» y se contradecía con la lista de clientes de aquí debajo. */}
      <p className='text-sm text-slate-500'>
        El repertorio de ClaimUp: los textos que ofrecemos a todos los clientes.{' '}
        <strong>Estar aquí no lo pone en marcha en ningún sitio.</strong> Se añade cliente por
        cliente, y enviarlo a Meta para que lo revise es otro paso aparte.
      </p>

      {plantillas.length === 0 && (
        <div className='bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500'>
          El catálogo está vacío. Se siembra con{' '}
          <code className='text-xs'>node scripts/seedKapsoTemplates.js --escribir</code>.
        </div>
      )}

      {plantillas.map(p => (
        <div key={p.key} className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
          <button
            type='button'
            onClick={e => {
              setAbierta(abierta === p.key ? null : p.key);
              e.currentTarget.blur();
            }}
            className='w-full text-left px-4 py-3 hover:bg-slate-50 outline-none focus:outline-none'
          >
            <div className='flex items-center justify-between gap-3'>
              <div className='min-w-0'>
                <p className='text-sm font-medium text-slate-900'>{p.description || p.key}</p>
                <p className='text-xs text-slate-400 font-mono'>{p.key}</p>
              </div>
              <div className='flex items-center gap-2 flex-shrink-0'>
                {p.unknown_variables?.length > 0 && (
                  <span className='px-2 py-0.5 rounded-lg text-[11px] font-medium bg-amber-100 text-amber-700'>
                    {p.unknown_variables.length} variable(s) sin datos
                  </span>
                )}
                <span className='text-xs text-slate-400'>
                  {p.registrations.length} cliente(s)
                </span>
                <span className='text-slate-400 text-xs'>{abierta === p.key ? '▲' : '▼'}</span>
              </div>
            </div>
          </button>

          {abierta === p.key && (
            // Se recarga entero en vez de fusionar la respuesta: al cambiar el
            // catálogo cambia también la deriva de cada cliente, que la
            // respuesta de guardar no trae.
            <Editor plantilla={p} companies={companies} onGuardada={cargar} />
          )}
        </div>
      ))}
    </div>
  );
};

export default KapsoCatalogue;
